import React, { useState, useRef, useMemo, useEffect } from "react";
import {
  colors2,
  icons,
  List,
  RadioButton,
  UI_BODY_SEMIBOLD_SM,
  UI_BODY_SM_DF,
  UI_CAPTION,
} from "oolib";
import styled from "styled-components";
import { SegmentedControl } from "@/components/UI/SegmentedControl";
import { DynamicOptionsLightboxShell } from "@/components/inputs/AccordionMultiSelect/comps/OptionsLightbox/DynamicOptionsLightboxShell";
import { SearchActionsToolbar } from "@/components/UI/DrilldownList";
import { DataFieldInputSelector } from "@/components/DataFieldInputSelector";

const { Eye } = icons;

// ─── Styled ──────────────────────────────────────────────

const StyledLabelCalcRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 8px 12px;
  margin-top: 10px;
  background: #F7F7F7;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background: ${colors2.grey10};
  }
`;

const StyledDynamicSection = styled.div`
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const StyledRadioRow = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  font-family: inherit;
  cursor: pointer;
  text-align: left;
  margin-bottom: 8px;
`;


// ─── Constants ───────────────────────────────────────────

const MIN_N = 1;
const MAX_N = 50;
const DEFAULT_N = 3;

// ─── Helpers ─────────────────────────────────────────────

const filterOptions = (options, searchTerm) => {
  if (!searchTerm?.trim()) return options;
  const term = searchTerm.toLowerCase().trim();
  return options.filter(
    (opt) => (opt.display || "").toLowerCase().includes(term)
  );
};

/**
 * Derive modifier label from current visibility state.
 * - All visible: "Show all values"
 * - Some hidden: "3/10 (Caveira, Legion, Black Beard)"
 */
/**
 * @param mode          "manual" | "dynamic"
 * @param dynamicPreset "topN" | "bottomN" — only consulted in dynamic mode
 *
 * Takes both rather than one overloaded value: the two vocabularies used to be
 * jammed into a single `preset` field, and passing the wrong one silently
 * produced the manual label while a Top-N was active.
 */
export const getVisibilityModifierLabel = (options, hiddenValues, mode, nValue, dynamicPreset, labels = {}) => {
  // `labels` lets a widget whose eye means "include" rather than "show" swap the
  // two manual strings without re-implementing this branch (the metric card's
  // value picker: "All values selected" / "Select options (required)").
  const allSelectedLabel = labels.allSelected || "Show all values";
  if (mode === "dynamic") {
    return dynamicPreset === "bottomN"
      ? `Show bottom ${nValue} values`
      : `Show top ${nValue} values`;
  }
  if (!options?.length) return labels.noneSelected || allSelectedLabel;
  if (!hiddenValues?.length) return allSelectedLabel;
  const hiddenKeySet = new Set();
  hiddenValues.forEach((h) => {
    if (h && typeof h === "object") {
      if (h.value !== undefined && h.value !== null) hiddenKeySet.add(h.value);
      if (h.display !== undefined && h.display !== null) hiddenKeySet.add(h.display);
    } else if (h !== undefined && h !== null) {
      hiddenKeySet.add(h);
    }
  });
  const visibleOptions = options.filter(
    (o) => !hiddenKeySet.has(o.value) && !hiddenKeySet.has(o.display)
  );
  if (!visibleOptions.length && labels.noneSelected) return labels.noneSelected;
  const visibleNames = visibleOptions.map((o) => o.display);
  return `${visibleOptions.length}/${options.length} - ${visibleNames.join(", ")}`;
};


// ─── Lightbox Content (used as renderContent for SearchableLightbox) ─────

/**
 * Creates a renderContent function for SearchableLightbox
 * that renders the Show/Hide Options two-tab UI.
 *
 * @param {Object} config
 * @param {Array}    config.options       — All available options [{ display, value }]
 * @param {string[]} config.hiddenValues  — Currently hidden display values
 * @param {string}   config.mode          — "manual" | "dynamic" — which state drives the chart
 * @param {string}   config.dynamicPreset — "topN" | "bottomN"
 * @param {number}   config.nValue        — The N for top/bottom N
 * @param {Function} config.onManualChange   — (newHidden: string[]) => void
 * @param {Function} config.onDynamicChange  — (dynamicPreset: string, n: number) => void
 * @param {Function} config.onModeChange     — (mode: string) => void
 */
export const createVisibilityLightboxContent = (config) => {
  return ({ onClose }) => (
    <VisibilityLightboxContent {...config} onClose={onClose} />
  );
};

const VisibilityLightboxContent = ({
  options = [],
  hiddenValues = [],
  mode = "manual",
  dynamicPreset = "topN",
  nValue = DEFAULT_N,
  onManualChange,
  onDynamicChange,
  onModeChange,
  onClose,
}) => {
  // The active tab IS the persisted mode — no local mirror to fall out of sync,
  // and nothing to re-derive when the lightbox remounts.
  const activeTab = mode === "dynamic" ? "dynamic" : "manual";
  const [searchTerm, setSearchTerm] = useState("");
  const clampedDefault = Math.min(DEFAULT_N, options.length || DEFAULT_N);
  const [topNValue, setTopNValue] = useState(dynamicPreset === "topN" ? nValue : clampedDefault);
  const [bottomNValue, setBottomNValue] = useState(dynamicPreset === "bottomN" ? nValue : clampedDefault);
  const [sortOrder, setSortOrder] = useState(null); // null | "asc" | "desc"
  const [selectedFirst, setSelectedFirst] = useState(false);

  const hiddenKeySet = useMemo(() => {
    const set = new Set();
    (hiddenValues || []).forEach((h) => {
      if (h && typeof h === "object") {
        if (h.value !== undefined && h.value !== null) set.add(h.value);
        if (h.display !== undefined && h.display !== null) set.add(h.display);
      } else if (h !== undefined && h !== null) {
        set.add(h);
      }
    });
    return set;
  }, [hiddenValues]);

  const visibleValues = useMemo(() => {
    return options.filter((o) => !hiddenKeySet.has(o.value) && !hiddenKeySet.has(o.display));
  }, [options, hiddenKeySet]);

  const filteredOptions = useMemo(() => {
    let result = filterOptions(options, searchTerm);

    if (sortOrder) {
      result = [...result].sort((a, b) => {
        const cmp = (a.display || "").localeCompare(b.display || "");
        return sortOrder === "asc" ? cmp : -cmp;
      });
    }

    if (selectedFirst) {
      const selectedSet = new Set(visibleValues.map(v => v.value));
      result = [...result].sort((a, b) => {
        const aSelected = selectedSet.has(a.value) ? 0 : 1;
        const bSelected = selectedSet.has(b.value) ? 0 : 1;
        return aSelected - bSelected;
      });
    }

    return result;
  }, [options, searchTerm, sortOrder, selectedFirst, visibleValues]);

  // ── Manual handlers ──

  const handleListChange = (_, item) => {
    const isCurrentlyVisible = visibleValues.some(
      (v) => v.value === item.value
    );
    const visibleValueSet = new Set(visibleValues.map((v) => v.value));
    if (isCurrentlyVisible) {
      visibleValueSet.delete(item.value);
    } else {
      visibleValueSet.add(item.value);
    }
    // Derive newHidden from options minus newly-visible. Always pure string
    // form so we don't carry stale {display,value} objects from a prior
    // dynamic preset into the manual list.
    const newHidden = options
      .filter((o) => !visibleValueSet.has(o.value))
      .map((o) => o.value);
    onManualChange(newHidden);
  };

  // Select-all / Clear-all act only on the current search results (filteredOptions);
  // other options keep their hidden state. With no search, filteredOptions === options
  // so these reduce to acting on everything. Invert is disabled while searching (toolbar).
  const handleSelectAll = () => {
    const filteredSet = new Set(filteredOptions.map((o) => o.value));
    const visibleSet = new Set(visibleValues.map((v) => v.value));
    const newHidden = options
      .filter((o) => !visibleSet.has(o.value) && !filteredSet.has(o.value))
      .map((o) => o.value);
    onManualChange(newHidden);
  };

  const handleClearAll = () => {
    const visibleSet = new Set(visibleValues.map((v) => v.value));
    const currentHidden = options.filter((o) => !visibleSet.has(o.value)).map((o) => o.value);
    const newHidden = [...new Set([...currentHidden, ...filteredOptions.map((o) => o.value)])];
    onManualChange(newHidden);
  };

  const handleInvertSelection = () => {
    const newHidden = visibleValues.map((v) => v.value);
    onManualChange(newHidden);
  };

  const handleSort = () => {
    setSortOrder(prev => prev === "asc" ? "desc" : "asc");
  };

  const handleShowSelectedFirst = () => {
    setSelectedFirst(prev => !prev);
  };

  // ── Dynamic handlers ──

  const handleDynamicPreset = (newPreset) => {
    onDynamicChange(newPreset, newPreset === "topN" ? topNValue : bottomNValue);
  };

  const handleTopNChange = (newN) => {
    setTopNValue(newN);
    onDynamicChange("topN", newN);
  };

  const handleBottomNChange = (newN) => {
    setBottomNValue(newN);
    onDynamicChange("bottomN", newN);
  };

  return (
    <div key={activeTab} style={{ padding: '1rem' }}>
      {/* Header */}
      <UI_BODY_SEMIBOLD_SM>
        Show/Hide Options
      </UI_BODY_SEMIBOLD_SM>
      {/* Hiding only stops drawing a value: with India 50% / China 50%, hiding
          China leaves India at 50%. A filter would make India 100%. */}
      <UI_CAPTION style={{ color: colors2.grey60, marginTop: "4px" }}>
        Hidden values still count towards percentages.
      </UI_CAPTION>

      {/* Tabs */}
      <SegmentedControl
        style={{ marginTop: "8px" }}
        options={[
          { display: "Manual", value: "manual" },
          { display: "Dynamic", value: "dynamic" },
        ]}
        value={activeTab}
        // Switching tabs is just "which state drives the chart". Both states
        // are stored independently, so there is nothing to stash or restore.
        onChange={(val) => onModeChange(val)}
      />

      {/* Manual Tab */}
      {activeTab === "manual" && (
        <div style={{
          marginTop: "10px",
          border: `1px solid ${colors2.grey10}`,
          borderRadius: "8px",
        }}>
          <SearchActionsToolbar
            search={{ value: searchTerm, onChange: (v) => setSearchTerm(v) }}
            selectAll={{ display: "Select all", onClick: handleSelectAll, icon: "CheckCircle" }}
            clearAll={{ display: "Clear all", onClick: handleClearAll, icon: "XCircle" }}
            invertSelection={{ onClick: handleInvertSelection, icon: "Shuffle", tooltip: "Invert selection" }}
            sort={{
              onClick: handleSort,
              icon: "SortDescending",
              tooltip: sortOrder === "desc" ? "Sort ascending" : "Sort descending",
            }}
            showSelectedFirst={{
              onClick: handleShowSelectedFirst,
              icon: "ListNumbers",
              active: selectedFirst,
              tooltip: "Show selected first",
            }}
          />
          <List
            disableShadow
            isMulti
            options={filteredOptions}
            value={visibleValues}
            onChange={handleListChange}
          />
        </div>
      )}

      {/* Dynamic Tab */}
      {activeTab === "dynamic" && (
        <StyledDynamicSection>
          <div>
            <StyledRadioRow onClick={() => handleDynamicPreset("topN")}>
              <RadioButton isSelected={dynamicPreset === "topN"} S />
              <UI_BODY_SM_DF style={{ color: colors2.black }}>
                Show Top N Values
              </UI_BODY_SM_DF>
            </StyledRadioRow>
            <DataFieldInputSelector
              stepper={{
                label: "Number of Values",
                value: topNValue,
                onChange: handleTopNChange,
                min: MIN_N,
                max: Math.min(MAX_N, options.length),
                disabled: dynamicPreset !== "topN",
              }}
            />
          </div>
          <div>
            <StyledRadioRow onClick={() => handleDynamicPreset("bottomN")}>
              <RadioButton isSelected={dynamicPreset === "bottomN"} S />
              <UI_BODY_SM_DF style={{ color: colors2.black }}>
                Show Bottom N Values
              </UI_BODY_SM_DF>
            </StyledRadioRow>
            <DataFieldInputSelector
              stepper={{
                label: "Number of Values",
                value: bottomNValue,
                onChange: handleBottomNChange,
                min: MIN_N,
                max: Math.min(MAX_N, options.length),
                disabled: dynamicPreset !== "bottomN",
              }}
            />
          </div>
        </StyledDynamicSection>
      )}
    </div>
  );
};

// ─── Modifier-row trigger + lightbox (matches DataFieldInputSelector modifier pattern) ──

const StyledModifierTrigger = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 1rem;
  min-height: 28px;
  background: #EFEFEF;
  cursor: pointer;
  color: ${colors2.grey80};
  transition: background 0.15s;

  &:hover {
    background: ${colors2.white};
  }
`;

const StyledModifierTriggerLabel = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  mask-image: linear-gradient(to right, black calc(100% - 24px), transparent 100%);
  -webkit-mask-image: linear-gradient(to right, black calc(100% - 24px), transparent 100%);
`;

const StyledModifierTriggerCaret = styled.div`
  display: flex;
  transition: color 0.15s;
  color: ${colors2.grey40};

  ${StyledModifierTrigger}:hover & {
    color: ${colors2.grey80};
  }
`;

export const ShowHideOptions = ({
  options = [],
  hiddenValues = [],
  mode = "manual",
  dynamicPreset = "topN",
  nValue = DEFAULT_N,
  onManualChange,
  onDynamicChange,
  onModeChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);

  const label = getVisibilityModifierLabel(options, hiddenValues, mode, nValue, dynamicPreset);

  if (!options.length) return null;

  return (
    <>
      <StyledModifierTrigger ref={triggerRef} onClick={() => setIsOpen(!isOpen)}>
        <Eye size={14} weight="bold" />
        <StyledModifierTriggerLabel>
          <UI_BODY_SEMIBOLD_SM>{label}</UI_BODY_SEMIBOLD_SM>
        </StyledModifierTriggerLabel>
        <StyledModifierTriggerCaret>
          <icons.CaretRight size={12} weight="bold" color="currentColor" />
        </StyledModifierTriggerCaret>
      </StyledModifierTrigger>

      {isOpen && (
        <DynamicOptionsLightboxShell
          onClickOutside={() => setIsOpen(false)}
          ref_anchorPoint={triggerRef}
          isMobile={false}
          width={{ desktop: 300, mobile: 300 }}
        >
          <VisibilityLightboxContent
            options={options}
            hiddenValues={hiddenValues}
            mode={mode}
            dynamicPreset={dynamicPreset}
            nValue={nValue}
            onManualChange={onManualChange}
            onDynamicChange={onDynamicChange}
            onModeChange={onModeChange}
            onClose={() => setIsOpen(false)}
          />
        </DynamicOptionsLightboxShell>
      )}
    </>
  );
};
